import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdBanner from '../components/AdBanner';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CATEGORIES = [
  { value: '', label: 'All Posts' },
  { value: 'invoicing', label: 'Invoicing Basics & Best Practices' },
  { value: 'bill-of-sale', label: 'Bill of Sale Articles' },
  { value: 'online-safety', label: 'Online Safety & Scam Prevention' },
  { value: 'buying-selling', label: 'Buying and Selling Tips' },
  { value: 'business', label: 'General Business Advice' },
  { value: 'thrifting', label: 'Thrifting to Marketplace Pipeline' }
];

function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);

  const currentCategory = searchParams.get('category') || '';
  const currentPage = parseInt(searchParams.get('page')) || 1;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '9'
      });
      if (currentCategory) {
        params.append('category', currentCategory);
      }

      const response = await fetch(`${API_URL}/blog?${params}`);
      if (!response.ok) throw new Error('Failed to fetch posts');

      const data = await response.json();
      setPosts(data.posts);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentCategory, currentPage]);

  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/blog/recent?limit=5`);
        if (response.ok) {
          const data = await response.json();
          setRecentPosts(data);
        }
      } catch (err) {
        console.error('Error fetching recent posts:', err);
      }
    };

    fetchPosts();
    fetchRecentPosts();
  }, [fetchPosts]);

  const handleCategoryChange = (category) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (value) => {
    const cat = CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  return (
    <div className="blog-page">
      <Helmet>
        <title>Blog - PrivateSold | Tips for Buying & Selling Safely</title>
        <meta name="description" content="Expert tips, guides, and news about buying and selling items safely on Craigslist, Facebook Marketplace, and other platforms. Learn about bills of sale, invoicing, and more." />
        <meta property="og:title" content="Blog - PrivateSold" />
        <meta property="og:description" content="Expert tips and guides for safe private sales transactions." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://privatesold.com/blog" />
      </Helmet>

      <div className="blog-hero">
        <div className="container">
          <h1>PrivateSold Blog</h1>
          <p>Tips, guides, and insights for buying and selling safely</p>
        </div>
      </div>

      <div className="blog-container container">
        <div className="blog-main">
          {/* Category Filter */}
          <div className="blog-filters">
            <div className="category-tabs">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  className={`category-tab ${currentCategory === cat.value ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(cat.value)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Ad Banner */}
          <AdBanner position="blog-top" />

          {/* Posts Grid */}
          {loading ? (
            <div className="blog-loading">
              <div className="loading-spinner"></div>
              <p>Loading posts...</p>
            </div>
          ) : error ? (
            <div className="blog-error">
              <p>Error loading posts: {error}</p>
              <button onClick={fetchPosts}>Try Again</button>
            </div>
          ) : posts.length === 0 ? (
            <div className="blog-empty">
              <h2>No posts found</h2>
              <p>Check back soon for new content!</p>
            </div>
          ) : (
            <div className="blog-grid">
              {posts.map((post, index) => (
                <React.Fragment key={post._id}>
                  <article className="blog-card">
                    {post.featuredImage?.url && (
                      <Link to={`/blog/${post.slug}`} className="blog-card-image">
                        <img src={post.featuredImage.url} alt={post.featuredImage.alt || post.title} />
                      </Link>
                    )}
                    <div className="blog-card-content">
                      <div className="blog-card-meta">
                        <span className="blog-card-category">{getCategoryLabel(post.category)}</span>
                        <span className="blog-card-date">{formatDate(post.publishDate)}</span>
                      </div>
                      <h2>
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h2>
                      {post.excerpt && (
                        <p className="blog-card-excerpt">{post.excerpt}</p>
                      )}
                      <Link to={`/blog/${post.slug}`} className="blog-card-link">
                        Read More →
                      </Link>
                    </div>
                  </article>
                  {/* Insert ad after every 3rd post */}
                  {(index + 1) % 3 === 0 && index < posts.length - 1 && (
                    <div className="blog-grid-ad">
                      <AdBanner position="blog-inline" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="blog-pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ← Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= pagination.pages}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="blog-sidebar">
          <div className="sidebar-widget">
            <h3>Recent Posts</h3>
            <ul className="recent-posts-list">
              {recentPosts.map(post => (
                <li key={post._id}>
                  <Link to={`/blog/${post.slug}`}>
                    <span className="recent-post-title">{post.title}</span>
                    <span className="recent-post-date">{formatDate(post.publishDate)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="sidebar-widget">
            <h3>Categories</h3>
            <ul className="category-list">
              {CATEGORIES.filter(c => c.value).map(cat => (
                <li key={cat.value}>
                  <button onClick={() => handleCategoryChange(cat.value)}>
                    {cat.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="sidebar-widget sidebar-cta">
            <h3>Create Your Bill of Sale</h3>
            <p>Generate professional documents in minutes.</p>
            <Link to="/bill-of-sale" className="sidebar-cta-btn">Get Started Free</Link>
          </div>

          <AdBanner position="blog-sidebar" />
        </aside>
      </div>
    </div>
  );
}

export default Blog;
